// ----------------------------------------------------------------------------
// 🔹 TYPE DEFINITIONS (Matching C# DTOs from Backend Services)
// ----------------------------------------------------------------------------

// Authentication Service Types
export interface User {
  id: string;
  username: string;
  email: string;
  userRole: string;
  churchId: string | null;
  userLogo?: string;
  isEmailVerified?: boolean;
  adminChurches?: AdminChurchInfo[];
}

// Session and OAuth Types
export interface VerificationSessionData {
  email: string;
  userId?: string;
  sessionToken: string;
  name?: string;
  churchId?: string;
  timestamp?: number;
  userRole?: string;
}

export interface OAuthState {
  csrfToken: string;
  clientContext: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  confirmPassword?: string;
  userRole?: string;
}

export interface AdminChurchInfo {
  churchId: string;
  churchName: string;
  role: string;
}

export interface LoginResponse {
  token: string;
  id: string;
  name: string;
  email: string;
  userRole: string;
  churchId: string;
  userLogo?: string;
  refreshToken: {
    token: string;
    expires: string;
  };
  message?: string;
  adminChurches?: AdminChurchInfo[];
}

export interface ChangePasswordRequest {
  email: string;
  currentPassword: string;
  newPassword: string;
}

// Organization Service Types - Church
export interface Address {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

export interface ContactInfo {
  phone?: string;
  website?: string;
}

export interface Church {
  id: string;
  name: string;
  description?: string;
  address: Address;
  contactInfo: ContactInfo;
  adminUserId: string;
  logoUrl?: string;
  coverImageUrl?: string;
  memberCount: number;
  groupCount: number;
  isActive: boolean;
  isPortedToChurch: boolean;
  churchHubLinkedAt?: string | null;
  createdAt: string;
  updatedAt?: string;
  inviteCode?: string;
  inviteCodeExpiry?: string;
  deletedAt?: string | null;
  deletedBy?: string | null;
  setlistSettings?: SetlistSettings;
}

export type LyricsPublishPolicy = 'prompt' | 'block' | 'off';

export interface SetlistSettings {
  lyricsRecipientEmail?: string;
  lyricsPublishPolicy: LyricsPublishPolicy;
  sendLyricsOnPublish: boolean;
}

export interface ChurchCreateRequest {
  name: string;
  description?: string;
  address: Address;
  contactInfo: ContactInfo;
  adminUserId: string;
}

export interface ChurchUpdateRequest {
  name: string;
  description?: string;
  address: Address;
  contactInfo: ContactInfo;
  logoUrl?: string;
  coverImageUrl?: string;
  setlistSettings?: SetlistSettings;
}

// Organization Service Types - Song
export interface Song {
  id: string;
  churchId: string;
  groupId?: string;
  title: string;
  author: string;
  ccliNumber?: string;
  tags: string[];
  defaultKey?: string;
  defaultTempo?: number;
  youtubeUrl?: string;
  lyrics?: string;
  lastPlayed?: string;
  timesSung?: number; // Completed services this song was in; recomputed server-side
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
  isActive: boolean;
}

export interface SongUsageRecomputeResult {
  plansScanned: number;
  songsUpdated: number;
  songsCreated: number;
  songsSkippedByLimit: number;
  itemsMatchedByTitle: number;
  itemsMatchedByLyrics: number;
}

export interface CreateSongDto {
  churchId: string;
  groupId?: string;
  title: string;
  author: string;
  ccliNumber?: string;
  tags?: string[];
  defaultKey?: string;
  defaultTempo?: number;
  youtubeUrl?: string;
  lyrics?: string;
  createdBy: string;
}

export interface UpdateSongDto {
  title?: string;
  author?: string;
  ccliNumber?: string;
  tags?: string[];
  defaultKey?: string;
  defaultTempo?: number;
  youtubeUrl?: string;
  lyrics?: string;
  lastPlayed?: string;
}

export interface SongSummaryDto {
  id: string;
  title: string;
  author: string;
  ccliNumber?: string;
  tags: string[];
  lastPlayed?: string;
  defaultKey?: string;
  defaultTempo?: number;
  youtubeUrl?: string;
}

// Organization Service Types - Membership
export enum ChurchMembershipState {
  None = 0,
  Pending = 1,
  Active = 2,
}

export enum MembershipScope {
  Church = "Church",
  Group = "Group",
}

export interface GroupAssignment {
  groupId: string;
  role: string;
  joinedAt: string;
}

export interface Membership {
  id: string;
  userId: string;
  churchId: string;
  group?: GroupAssignment;
  role: string;
  function?: string;
  joinedAt: string;
  createdAt?: string;
  isActive: boolean;
  churchState: ChurchMembershipState;
  scope?: MembershipScope;
  /** The team they asked to join with a team invite code, until they're placed on a team. */
  pendingGroupId?: string | null;
  /** Approval responses only: what happened to the team they asked for. */
  teamAssignment?: TeamAssignmentResult | null;
}

/** Returned by approval when the person had asked to join a team. */
export interface TeamAssignmentResult {
  status: 'assigned' | 'team_full' | 'team_unavailable' | 'failed';
  groupId: string;
  groupName?: string | null;
  message?: string | null;
}

export interface ChurchMembershipCreateRequest {
  userId: string;
  churchId: string;
  role?: string;
  initialState?: ChurchMembershipState;
  addedBy: string;
}

export interface GroupMembershipCreateRequest {
  userId: string;
  churchId: string;
  groupId: string;
  role?: string;
  addedBy: string;
}

export interface UserMembershipStatusResponse {
  userId: string;
  churchId: string;
  churchMembershipState: ChurchMembershipState;
  isInGroup: boolean;
  activeGroupCount: number;
  currentGroupCount: number;
  maxGroupLimit: number;
  maxGroupCount: number;
  canJoinAnotherGroup: boolean;
  canJoinMoreGroups: boolean;
  groupMembershipIds: string[];
}

export interface MembershipDetailResponse {
  id: string;
  userId: string;
  churchId: string;
  group?: GroupAssignment;
  groupName: string;
  groupType?: string;
  role: string;
  joinedAt: string;
  isActive: boolean;
  churchState: ChurchMembershipState;
}

export interface EnrichedMembership {
  id: string;
  userId: string;
  email: string;
  username: string;
  image?: string;
  churchId: string;
  group?: GroupAssignment;
  role: string;
  function?: string;
  isActive: boolean;
  churchState: ChurchMembershipState;
  joinedAt: string;
  /** The team from the invite code they used, until they're placed on a team. */
  pendingGroupId?: string | null;
}

// Organization Service Types - Service Plan
export interface PlanItem {
  id: string;
  type: string; // "Song", "Prayer", "Scripture", etc.
  title: string;
  duration?: number;
  notes?: string;
  songId?: string;
  key?: string;
  section?: string; // Custom section label (e.g. "Call to Worship", "Praise")
  orderIndex?: number;
  lyrics?: string;
  youtubeUrl?: string;
}

export interface TeamMemberAssignment {
  id: string;
  userId: string;
  name: string;
  role: string; // "Worship Leader", "Vocals", "Guitar", "Drums", etc.
  status: number; // 0 = Pending, 1 = Confirmed, 2 = Declined
  isPlaceholder?: boolean;
}

export interface ServicePlan {
  id: string;
  churchId: string;
  groupId?: string;
  date: string;
  title: string;
  notes?: string;
  duration?: string;
  serviceType?: string;
  items: PlanItem[];
  team: TeamMemberAssignment[];
  isPublished: boolean;
  isSetlistPublished?: boolean;
  setlistModeOverride?: string; // null = use group default
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  isActive: boolean;
}

export interface CreateServicePlanDto {
  churchId: string;
  groupId?: string;
  date: string;
  title: string;
  notes?: string;
  duration?: string;
  serviceType?: string;
  items: PlanItem[];
  team: TeamMemberAssignment[];
  isPublished?: boolean;
  createdBy: string;
}

export interface UpdateServicePlanDto {
  date?: string;
  title?: string;
  notes?: string;
  duration?: string;
  serviceType?: string;
  items?: PlanItem[];
  team?: TeamMemberAssignment[];
  isPublished?: boolean;
  isSetlistPublished?: boolean;
  setlistModeOverride?: string;
}

export interface ServicePlanSummaryDto {
  id: string;
  date: string;
  title: string;
  songCount: number;
  teamMemberCount: number;
  isPublished: boolean;
}

// Organization Service Types - Group
export interface Group {
  id: string;
  churchId: string;
  name: string;
  description?: string;
  groupType?: string;
  leaderId?: string;
  leaderIds?: string[];
  memberCount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  inviteCode?: string;
  inviteCodeExpiry?: string;
  meetingSchedule?: string;
  meetingLocation?: string;
  maxCapacity?: number;
  capacity?: number; // Dynamically computed: available slots from plan limits
  codePrefix?: string;
  setlistMode?: string; // "leader_builds" | "admin_predefined"
}

export interface GroupCreateRequest {
  churchId: string;
  name: string;
  description?: string;
  type?: string;
  leaderIds?: string[];
  meetingSchedule?: string;
  meetingLocation?: string;
  maxCapacity?: number;
  createdBy: string;
  codePrefix?: string;
  setlistMode?: string;
}

export interface GroupUpdateRequest {
  name?: string;
  description?: string;
  type?: string;
  meetingSchedule?: string;
  meetingLocation?: string;
  maxCapacity?: number;
  /** True removes the size limit (maxCapacity left out keeps the current one). */
  clearMaxCapacity?: boolean;
  leaderIds?: string[];
  updatedBy?: string;
  setlistMode?: string;
}

export interface GroupInviteCodeResponse {
  groupId: string;
  inviteCode: string;
  expiresAt: string;
}

export interface JoinGroupByCodeRequest {
  inviteCode: string;
  userId: string;
}

// ─── Member Availability ───────────────────────────────────────────────────

export type AvailabilityStatusValue = 'Available' | 'Tentative' | 'Blocked';

export interface AvailabilityDay {
  /** Day of the month, 1-31. */
  day: number;
  status: AvailabilityStatusValue;
}

/**
 * One team member's availability for a month. `days` only lists days that
 * deviate from the default; any day not present is "Available".
 */
export interface MemberAvailabilityDto {
  userId: string;
  userName: string;
  role: string;
  year: number;
  /** Calendar month, 1-12. */
  month: number;
  days: AvailabilityDay[];
  /** True when an admin last set this member's availability on their behalf. */
  updatedByAdmin?: boolean;
  /**
   * True when the member is on long-term / indefinite leave from this team.
   * Their `days` come back Blocked for the whole month (so the member app
   * treats them as away with no app change); admins can grey them out of the
   * roster builder instead of re-marking them away every month.
   */
  onLeave?: boolean;
  /** Optional admin-only note for the leave (e.g. "Sabbatical"). */
  leaveReason?: string | null;
}

export interface MergePreviewResponse {
  sourceChurchId: string;
  sourceChurchName: string;
  targetChurchId: string;
  targetChurchName: string;
  targetGroupId: string;
  targetGroupName: string;
  songCount: number;
  planCount: number;
  memberCount: number;
}

export interface MergeIntoGroupRequest {
  sourceChurchId: string;
  targetGroupId: string;
  requestedBy: string;
}

export interface MergeIntoGroupResponse {
  songsMigrated: number;
  plansMigrated: number;
  membersMigrated: number;
  sourceChurchDeactivated: boolean;
}

export interface PortToChurchResponse {
  church: Church;
}

export interface ChurchHubAdminSummary {
  userId: string;
  email: string;
  username: string;
  role: string;
}

export interface ChurchHubStatusResponse {
  isLinked: boolean;
  linkedAt?: string | null;
  memberCount: number;
  groupCount: number;
  admins: ChurchHubAdminSummary[];
}

/**
 * @deprecated Invite by email is retired in the Worship Planner admin UI;
 * invites are by team code only. See DEPRECATED.md.
 */
export interface EmailInviteRequest {
  email: string;
  churchId: string;
  groupId: string;
  role: string;
}

// Setlist Templates
export interface SetlistTemplate {
  id: string;
  churchId: string;
  groupId?: string;
  name: string;
  items: PlanItem[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSetlistTemplateDto {
  name: string;
  groupId?: string;
  items: PlanItem[];
}

export interface UpdateSetlistTemplateDto {
  name?: string;
  items?: PlanItem[];
}
